import React, { useMemo } from "react";
import { Box, Chip, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import CandlestickChartRoundedIcon from "@mui/icons-material/CandlestickChartRounded";
import StackedLineChartRoundedIcon from "@mui/icons-material/StackedLineChartRounded";
import { keyframes } from "@emotion/react";
import { isDarkMode } from "../../utils/constants";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const STAGES = [
  { key: "plan", label: "Plan" },
  { key: "evidence", label: "Evidence" },
  { key: "specialists", label: "Analysts" },
  { key: "synthesis", label: "Synthesis" },
  { key: "audit", label: "Check" },
];

const statusColors = (dark) => ({
  pending: {
    border: dark ? "rgba(148,163,184,0.22)" : "rgba(100,116,139,0.18)",
    bg: dark ? "rgba(15,23,42,0.48)" : "rgba(248,250,252,0.9)",
    fg: dark ? "#94A3B8" : "#64748B",
  },
  running: {
    border: dark ? "rgba(56,189,248,0.46)" : "rgba(2,132,199,0.28)",
    bg: dark ? "rgba(8,47,73,0.44)" : "rgba(240,249,255,0.96)",
    fg: dark ? "#7DD3FC" : "#0369A1",
  },
  complete: {
    border: dark ? "rgba(74,222,128,0.38)" : "rgba(22,163,74,0.24)",
    bg: dark ? "rgba(20,83,45,0.36)" : "rgba(240,253,244,0.96)",
    fg: dark ? "#86EFAC" : "#15803D",
  },
  warning: {
    border: dark ? "rgba(251,191,36,0.46)" : "rgba(217,119,6,0.28)",
    bg: dark ? "rgba(120,53,15,0.36)" : "rgba(255,251,235,0.98)",
    fg: dark ? "#FCD34D" : "#B45309",
  },
  failed: {
    border: dark ? "rgba(248,113,113,0.45)" : "rgba(220,38,38,0.25)",
    bg: dark ? "rgba(127,29,29,0.34)" : "rgba(254,242,242,0.98)",
    fg: dark ? "#FCA5A5" : "#B91C1C",
  },
});

const normalizeStatus = (status) => {
  const value = String(status || "").toLowerCase();
  if (["complete", "completed", "done"].includes(value)) return "complete";
  if (["warning", "skipped"].includes(value)) return "warning";
  if (["failed", "error"].includes(value)) return "failed";
  if (["running", "active"].includes(value)) return "running";
  return "pending";
};

const stageIcon = (stage, eventType) => {
  if (eventType === "coverage_warning") return HelpRoundedIcon;
  if (stage === "plan") return QueryStatsRoundedIcon;
  if (stage === "evidence") return TrackChangesRoundedIcon;
  if (stage === "specialists") return InsightsRoundedIcon;
  if (stage === "synthesis") return HubRoundedIcon;
  if (stage === "audit") return TuneRoundedIcon;
  if (stage === "response") return SpeedRoundedIcon;
  return StackedLineChartRoundedIcon;
};

const specialistIcon = (title = "") => {
  const text = title.toLowerCase();
  if (text.includes("price")) return CandlestickChartRoundedIcon;
  if (text.includes("fundamental")) return QueryStatsRoundedIcon;
  if (text.includes("catalyst")) return ArticleRoundedIcon;
  if (text.includes("peer")) return HubRoundedIcon;
  if (text.includes("macro")) return PublicRoundedIcon;
  if (text.includes("risk")) return TuneRoundedIcon;
  if (text.includes("option")) return ShowChartRoundedIcon;
  return InsightsRoundedIcon;
};

const statusIcon = (status) => {
  if (status === "complete") return CheckRoundedIcon;
  if (status === "failed") return CloseRoundedIcon;
  if (status === "warning") return HelpRoundedIcon;
  return AutorenewRounded;
};

const compactTime = (ms) => {
  const value = Number(ms);
  if (!value || Number.isNaN(value)) return null;
  if (value < 1000) return `${value} ms`;
  return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)}s`;
};

const eventKey = (event) =>
  event.event_id || `${event.event_type}-${event.stage}-${event.title}-${event.created_at}`;

const COMPLETES_EVENT = {
  market_plan_started: ["market_plan_completed", "market_plan_ineligible"],
  evidence_build_started: ["evidence_build_completed"],
  direct_data_started: ["direct_data_completed"],
  specialists_started: ["specialists_completed"],
  synthesis_started: ["synthesis_completed"],
  answer_stream_started: ["answer_stream_completed"],
};

const displayEvents = (events) => {
  const eventTypes = new Set(events.map((event) => event.event_type));
  const completedSpecialistPrefixes = new Set(
    events
      .filter((event) => ["specialist_completed", "specialist_failed"].includes(event.event_type))
      .map((event) => String(event.title || "").replace(/\s+(complete|failed)$/i, "").toLowerCase()),
  );

  return events.filter((event) => {
    const completionTypes = COMPLETES_EVENT[event.event_type];
    if (completionTypes?.some((eventType) => eventTypes.has(eventType))) return false;

    if (event.event_type !== "specialist_started") return true;
    const prefix = String(event.title || "").replace(/\s+running$/i, "").toLowerCase();
    return !completedSpecialistPrefixes.has(prefix);
  });
};

const StageStepper = ({ events }) => {
  const palette = statusColors(isDarkMode);
  const stageStatus = useMemo(() => {
    const next = {};
    STAGES.forEach((stage) => { next[stage.key] = "pending"; });
    events.forEach((event) => {
      const stage = event.stage;
      if (!next[stage]) return;
      const status = normalizeStatus(event.status);
      if (status === "failed") next[stage] = "failed";
      else if (status === "warning" && next[stage] !== "failed") next[stage] = "warning";
      else if (status === "running" && !["failed", "warning", "complete"].includes(next[stage])) next[stage] = "running";
      else if (status === "complete" && !["failed", "warning"].includes(next[stage])) next[stage] = "complete";
    });
    return next;
  }, [events]);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${STAGES.length}, 1fr)`, gap: "6px", mb: 1.4 }}>
      {STAGES.map((stage) => {
        const status = stageStatus[stage.key] || "pending";
        const colors = palette[status] || palette.pending;
        return (
          <Box
            key={stage.key}
            sx={{
              minWidth: 0,
              px: 0.7,
              py: 0.65,
              borderRadius: "7px",
              border: `1px solid ${colors.border}`,
              background: colors.bg,
              color: colors.fg,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "9px", fontWeight: 800, lineHeight: 1.1 }}>
              {stage.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const TraceChip = ({ label, tone = "default" }) => {
  const toneMap = {
    bullish: isDarkMode ? "#86EFAC" : "#15803D",
    bearish: isDarkMode ? "#FCA5A5" : "#B91C1C",
    mixed: isDarkMode ? "#FCD34D" : "#B45309",
    neutral: isDarkMode ? "#93C5FD" : "#1D4ED8",
    default: isDarkMode ? "#CBD5E1" : "#475569",
  };

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        borderRadius: "6px",
        fontSize: "10px",
        fontWeight: 700,
        color: toneMap[tone] || toneMap.default,
        backgroundColor: isDarkMode ? "rgba(15,23,42,0.78)" : "rgba(248,250,252,0.92)",
        border: `1px solid ${isDarkMode ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.16)"}`,
      }}
    />
  );
};

const TraceCard = ({ event, index }) => {
  const status = normalizeStatus(event.status);
  const palette = statusColors(isDarkMode);
  const colors = palette[status] || palette.pending;
  const isSpecialist = String(event.event_type || "").startsWith("specialist_");
  const MainIcon = isSpecialist ? specialistIcon(event.title) : stageIcon(event.stage, event.event_type);
  const StatusIcon = statusIcon(status);
  const stance = String(event.stance || "").toLowerCase();
  const duration = compactTime(event.duration_ms);
  const tickers = Array.isArray(event.ticker_symbols) ? event.ticker_symbols.slice(0, 5) : [];

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "8px",
        border: `1px solid ${colors.border}`,
        background: isDarkMode
          ? "linear-gradient(145deg, rgba(10,15,24,0.96), rgba(15,23,42,0.92))"
          : "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))",
        boxShadow: isDarkMode
          ? "0 14px 30px rgba(0,0,0,0.34)"
          : "0 12px 26px rgba(15,23,42,0.08)",
        p: 1.25,
        animation: `${cardIn} 260ms ease-out both`,
        animationDelay: `${Math.min(index, 8) * 28}ms`,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "0 auto 0 0",
          width: 3,
          background: colors.fg,
          opacity: 0.9,
        }}
      />

      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", minWidth: 0 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: colors.fg,
            background: colors.bg,
            border: `1px solid ${colors.border}`,
          }}
        >
          <MainIcon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
            <Typography
              sx={{
                fontSize: "13px",
                lineHeight: 1.25,
                fontWeight: 800,
                color: isDarkMode ? "#F8FAFC" : "#0F172A",
              }}
            >
              {event.title}
            </Typography>
            <Box sx={{ color: colors.fg, display: "flex", alignItems: "center", mt: "1px" }}>
              <StatusIcon
                sx={{
                  fontSize: 15,
                  animation: status === "running" ? `${spin} 1.2s linear infinite` : "none",
                }}
              />
            </Box>
          </Box>

          <Typography
            sx={{
              mt: 0.55,
              fontSize: "12px",
              lineHeight: 1.42,
              color: isDarkMode ? "rgba(226,232,240,0.88)" : "rgba(30,41,59,0.88)",
            }}
          >
            {event.summary}
          </Typography>

          {event.detail && status !== "running" && (
            <Typography
              sx={{
                mt: 0.75,
                fontSize: "12px",
                lineHeight: 1.42,
                color: isDarkMode ? "rgba(148,163,184,0.94)" : "rgba(71,85,105,0.94)",
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {event.detail}
            </Typography>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.55, mt: 0.95 }}>
            {event.stance && <TraceChip label={String(event.stance).replace(/_/g, " ")} tone={stance} />}
            {event.confidence && <TraceChip label={`${event.confidence} confidence`} />}
            {duration && <TraceChip label={duration} />}
            {tickers.map((ticker) => <TraceChip key={`${eventKey(event)}-${ticker}`} label={ticker} />)}
            {Array.isArray(event.ticker_symbols) && event.ticker_symbols.length > tickers.length && (
              <TraceChip label={`+${event.ticker_symbols.length - tickers.length}`} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const EmptyTraceState = ({ loading }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: "8px",
      border: `1px solid ${isDarkMode ? "rgba(148,163,184,0.16)" : "rgba(100,116,139,0.14)"}`,
      background: isDarkMode ? "rgba(15,23,42,0.54)" : "rgba(248,250,252,0.92)",
    }}
  >
    <Typography sx={{ fontSize: "13px", fontWeight: 800, color: isDarkMode ? "#F8FAFC" : "#0F172A" }}>
      {loading ? "Preparing analysis trace" : "No active analysis trace"}
    </Typography>
    <Typography sx={{ mt: 0.5, fontSize: "12px", lineHeight: 1.4, color: isDarkMode ? "#94A3B8" : "#64748B" }}>
      {loading
        ? "Trace initializing. Stage events will land here as they complete."
        : "No live market-analysis run."}
    </Typography>
  </Box>
);

const AnalysisTracePanel = ({ events = [], legacyContent = null, loading = false }) => {
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const aTime = Date.parse(a.created_at || "") || 0;
      const bTime = Date.parse(b.created_at || "") || 0;
      return aTime - bTime;
    });
  }, [events]);
  const visibleEvents = useMemo(() => displayEvents(sortedEvents), [sortedEvents]);
  const specialistCompleted = sortedEvents.filter((event) => event.event_type === "specialist_completed").length;
  const warningCount = sortedEvents.filter((event) => normalizeStatus(event.status) === "warning").length;
  const hasEvents = visibleEvents.length > 0;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        px: 1.1,
        py: 1.1,
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDarkMode ? "#334155" : "#CBD5E1",
          borderRadius: 8,
        },
      }}
    >
      <Box
        sx={{
          mb: 1.25,
          p: 1.25,
          borderRadius: "8px",
          border: `1px solid ${isDarkMode ? "rgba(56,189,248,0.2)" : "rgba(2,132,199,0.16)"}`,
          background: isDarkMode
            ? "linear-gradient(145deg, rgba(7,18,30,0.98), rgba(15,23,42,0.94))"
            : "linear-gradient(145deg, rgba(240,249,255,0.96), rgba(255,255,255,0.98))",
        }}
      >
        <Typography sx={{ fontSize: "10px", fontWeight: 900, letterSpacing: 1.1, textTransform: "uppercase", color: isDarkMode ? "#7DD3FC" : "#0369A1" }}>
          Live Market Desk
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: "15px", lineHeight: 1.25, fontWeight: 850, color: isDarkMode ? "#F8FAFC" : "#0F172A" }}>
          Analysis Trace
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: "12px", lineHeight: 1.4, color: isDarkMode ? "#CBD5E1" : "#475569" }}>
          Plan, evidence coverage, specialist views, synthesis, and quality checks.
        </Typography>
        <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mt: 1 }}>
          <TraceChip label={`${specialistCompleted} analysts complete`} />
          <TraceChip label={`${warningCount} data notes`} tone={warningCount ? "mixed" : "neutral"} />
        </Box>
      </Box>

      <StageStepper events={sortedEvents} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {!hasEvents && <EmptyTraceState loading={loading} />}
        {visibleEvents.map((event, index) => (
          <TraceCard key={eventKey(event)} event={event} index={index} />
        ))}
      </Box>

      {legacyContent && (
        <Box sx={{ mt: 1.4 }}>
          <Typography sx={{ mb: 0.7, fontSize: "10px", fontWeight: 850, letterSpacing: 0.9, textTransform: "uppercase", color: isDarkMode ? "#94A3B8" : "#64748B" }}>
            System Processes
          </Typography>
          {legacyContent}
        </Box>
      )}
    </Box>
  );
};

export default AnalysisTracePanel;
